<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="py-6 text-center">
      <svg class="animate-spin h-5 w-5 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <!-- Empty State -->
    <div v-else-if="jobs.length === 0" class="py-6 text-center text-sm text-gray-400">
      No queries yet
    </div>

    <!-- Job List -->
    <div v-else class="max-h-[300px] overflow-y-auto">
      <div
        v-for="job in jobs"
        :key="job.id"
        class="flex items-center justify-between py-2.5 px-1 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer rounded transition-colors"
        :class="{ 'bg-orange-50 hover:bg-orange-50': job.id === activeJobId }"
        @click="$emit('select', job)"
      >
        <div class="flex items-center flex-1 min-w-0 mr-3">
          <span
            class="shrink-0 mr-2 px-1.5 py-0.5 rounded text-xs font-medium"
            :class="typeBadgeClass(job.job_type)"
          >{{ typeBadgeLabel(job.job_type) }}</span>
          <span v-if="showEntity && job.entity_name" class="shrink-0 mr-2 text-xs text-gray-500 whitespace-nowrap">{{ job.entity_name }} —</span>
          <p class="text-sm text-gray-700 truncate">{{ job.query }}</p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <span v-if="showUser" class="text-xs text-gray-500 whitespace-nowrap">{{ job.user_name }}</span>
          <span
            class="px-1.5 py-0.5 rounded text-xs font-medium"
            :class="statusClass(job.status)"
          >
            {{ statusLabel(job.status) }}
          </span>
          <span class="text-xs text-gray-400 w-12 text-right">{{ formatDate(job.created_at) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnalystHistory',
  props: {
    jobs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    activeJobId: {
      type: [String, Number],
      default: null,
    },
    showUser: {
      type: Boolean,
      default: false,
    },
    showEntity: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select'],
  methods: {
    typeBadgeLabel(jobType) {
      const labels = {
        personal_analyst: 'Analysis',
        weekly_report: 'Report',
        pre_meeting_brief: 'Brief',
        peer_practice_playbook: 'Playbook',
      }
      return labels[jobType] || jobType
    },
    typeBadgeClass(jobType) {
      const classes = {
        personal_analyst: 'bg-blue-100 text-blue-700',
        weekly_report: 'bg-orange-100 text-orange-700',
        pre_meeting_brief: 'bg-green-100 text-green-700',
        peer_practice_playbook: 'bg-purple-100 text-purple-700',
      }
      return classes[jobType] || 'bg-gray-100 text-gray-700'
    },
    statusLabel(status) {
      const labels = {
        QUEUED: 'Queued',
        PROCESSING: 'Analyzing...',
        COMPLETED: 'Complete',
        FAILED: 'Failed',
      }
      return labels[status] || status
    },
    statusClass(status) {
      const classes = {
        QUEUED: 'bg-yellow-100 text-yellow-800',
        PROCESSING: 'bg-blue-100 text-blue-800',
        COMPLETED: 'bg-green-100 text-green-800',
        FAILED: 'bg-red-100 text-red-800',
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString()
    },
  },
}
</script>
